import React, { useState } from 'react';
import { useDirectUpload } from '@/contexts/DirectUploadContext';
import { X, Minimize2, Maximize2, Upload, CheckCircle, XCircle, RefreshCw, Trash2, Pause, Play, Wifi } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export const FloatingUploadProgress = () => {
  const {
    queue,
    cancelUpload,
    clearCompleted,
    pauseUpload,
    resumeUpload,
    totalProgress,
    uploadingCount,
    completedCount,
    failedCount,
    duplicateCount,
    pausedCount
  } = useDirectUpload();

  const [isMinimized, setIsMinimized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const total = queue.length;
  const pendingCount = queue.filter(q => q.status === 'pending').length;

  // Don't render if there's nothing in the queue
  if (total === 0) {
    return null;
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600';
      case 'failed': return 'text-red-600';
      case 'duplicate': return 'text-orange-600';
      case 'uploading': return 'text-blue-600';
      case 'paused': return 'text-yellow-600';
      case 'processing': return 'text-purple-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'duplicate': return <XCircle className="w-4 h-4 text-orange-600" />;
      case 'uploading': return <Upload className="w-4 h-4 text-blue-600 animate-pulse" />;
      case 'paused': return <Pause className="w-4 h-4 text-yellow-600" />;
      case 'processing': return <RefreshCw className="w-4 h-4 text-purple-600 animate-spin" />;
      default: return <Upload className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Complete';
      case 'failed': return 'Failed';
      case 'duplicate': return 'Duplicate - Skipped';
      case 'uploading': return 'Uploading...';
      case 'paused': return 'Paused';
      case 'processing': return 'Processing...';
      case 'pending': return 'Waiting...';
      default: return status;
    }
  };

  const cancelAll = () => {
    queue.forEach(item => {
      if (item.status !== 'completed' && item.status !== 'failed') {
        cancelUpload(item.uploadId);
      }
    });
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-24 right-4 z-50">
        <Card className="shadow-lg border border-slate-200">
          <button
            onClick={() => setIsMinimized(false)}
            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Upload className="w-5 h-5 text-[#BF212F]" />
            <div className="flex flex-col items-start">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {uploadingCount > 0 ? `Uploading ${uploadingCount}` : pausedCount > 0 ? `${pausedCount} Paused` : 'Upload Queue'}
              </span>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {completedCount} done, {failedCount} failed
              </span>
            </div>
            <Progress value={totalProgress} className="w-24 h-2" />
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed bottom-24 right-4 z-50 w-96">
      <Card className="shadow-2xl border border-slate-200">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 text-white font-tac-heading" style={{ background: '#0A0A0B' }}>
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5" style={{ color: '#BF212F' }} />
            <span className="font-semibold">{uploadingCount > 0 ? `Uploading ${uploadingCount} of ${total}` : 'Upload Queue'}</span>
            {pausedCount > 0 && (
              <span className="text-xs bg-yellow-500 px-2 py-0.5 rounded-full">
                {pausedCount} paused
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              title={isExpanded ? "Show less" : "Show more"}
            >
              {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setIsMinimized(true)}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              title="Minimize"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Overall Progress */}
        <div className="px-4 py-3 bg-gray-50 dark:bg-slate-800 border-b dark:border-slate-700">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {uploadingCount + pendingCount + pausedCount} / {total} remaining
            </span>
            <span className="text-gray-600 dark:text-gray-400">{totalProgress}%</span>
          </div>
          <Progress value={totalProgress} className="h-2" />
          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mt-2 flex-wrap gap-2">
            <span>✓ {completedCount} completed</span>
            {duplicateCount > 0 && <span className="text-orange-600">⚠ {duplicateCount} duplicates</span>}
            <span>✗ {failedCount} failed</span>
            {pausedCount > 0 && <span className="text-yellow-600">⏸ {pausedCount} paused</span>}
          </div>
        </div>

        {/* Action Buttons */}
        {(pendingCount > 0 || uploadingCount > 0 || completedCount > 0 || failedCount > 0) && (
          <div className="px-4 py-2 bg-gray-50 dark:bg-slate-800 border-b dark:border-slate-700 flex gap-2 flex-wrap">
            {(pendingCount > 0 || uploadingCount > 0) && (
              <Button
                size="sm"
                variant="destructive"
                onClick={cancelAll}
                className="text-xs"
              >
                <X className="w-3 h-3 mr-1" />
                Cancel All
              </Button>
            )}
            {completedCount > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={clearCompleted}
                className="text-xs"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Clear Completed
              </Button>
            )}
          </div>
        )}

        {/* File List - Show individual progress for each file */}
        <div className="max-h-96 overflow-y-auto">
          {queue.map((item) => (
            <div
              key={item.uploadId}
              className={`px-4 py-3 border-b dark:border-slate-700 last:border-b-0 transition-all duration-300 ${
                item.status === 'completed' ? 'bg-green-50 dark:bg-green-900/20 opacity-75' :
                item.status === 'duplicate' ? 'bg-orange-50 dark:bg-orange-900/20 opacity-75' :
                item.status === 'failed' ? 'bg-red-50 dark:bg-red-900/20' :
                item.status === 'paused' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                'hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  {getStatusIcon(item.status)}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {item.fileName}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                      <span>{formatFileSize(item.fileSize)}</span>
                      {item.canResume && (
                        <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                          <Wifi className="w-3 h-3" />
                          Resumable
                        </span>
                      )}
                      {item.status === 'completed' && (
                        <span className="text-green-600 dark:text-green-400">
                          • Auto-removing...
                        </span>
                      )}
                    </div>
                    {item.error && (
                      <div className={`text-xs mt-1 ${
                        item.status === 'duplicate' ? 'text-orange-600 dark:text-orange-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {item.error}
                      </div>
                    )}
                    {(item.status === 'uploading' || item.status === 'pending' || item.status === 'paused' || item.status === 'processing') && (
                      <div className="mt-2">
                        <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                          <span>{getStatusText(item.status)}</span>
                          <span>{item.progress || 0}%</span>
                        </div>
                        <Progress value={item.progress || 0} className="h-1" />
                        {item.bytesUploaded && item.bytesTotal && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {formatFileSize(item.bytesUploaded)} / {formatFileSize(item.bytesTotal)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {/* Pause/Resume Button */}
                  {item.status === 'uploading' && (
                    <button
                      onClick={() => pauseUpload(item.uploadId)}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
                      title="Pause upload"
                    >
                      <Pause className="w-4 h-4 text-yellow-600" />
                    </button>
                  )}
                  {item.status === 'paused' && (
                    <button
                      onClick={() => resumeUpload(item.uploadId)}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
                      title="Resume upload"
                    >
                      <Play className="w-4 h-4 text-green-600" />
                    </button>
                  )}
                  {/* Cancel Button */}
                  {(item.status === 'pending' || item.status === 'uploading' || item.status === 'paused') && (
                    <button
                      onClick={() => cancelUpload(item.uploadId)}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
                      title="Cancel"
                    >
                      <X className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Resumable Upload Notice */}
        {queue.some(item => item.canResume) && (
          <div className="px-4 py-2 bg-green-50 dark:bg-green-900/20 border-t dark:border-slate-700 text-xs text-green-700 dark:text-green-400 flex items-center gap-2">
            <Wifi className="w-3 h-3" />
            <span>Uploads can be resumed if interrupted</span>
          </div>
        )}
      </Card>
    </div>
  );
};
